import React from 'react';
import styled, { css, type DefaultTheme } from 'styled-components';

import Icon from '~/components/atom/Icon';
import { Body } from '~/components/atom/Typography';
import Link from '~/components/molecule/Link';
import TEST_ID from '../../index.testid';
import { LFA_PREFIX } from '~/components/page/Automation/v2/components/FlowList/components/FlowListTable';
import { useTimelineContext } from '~/components/template/EventTimelineV2/context';

const FLOW_BUILDER_UPDATE_PATH = '/-/automation/flows/builder-v2/update/';

const text = {
  via: 'via',
};

export type Props = {
  flowBlueprintId: string;
  flowBlueprintActionId: string;
};

const FlowActorLink: React.FC<Props> = ({
  flowBlueprintId,
  flowBlueprintActionId,
}) => {
  const { flows = [] } = useTimelineContext();

  if (!flowBlueprintActionId || !flowBlueprintId) return null;

  const matchedFlow = flows.find(flow => flow.id === flowBlueprintId);
  if (!matchedFlow) return null;

  const isLfaFlow = matchedFlow.name.startsWith(LFA_PREFIX);

  const content = (
    <>
      <Prefix size="s" inline margin={[null]}>
        {text.via}
      </Prefix>
      <StyledIcon name="flow" size="s" strokeWidth={2} />
      <FlowName size="s" margin={[null]} truncate>
        {matchedFlow.name}
      </FlowName>
    </>
  );

  if (isLfaFlow) {
    return (
      <StaticActor
        title={matchedFlow.name}
        data-testid={TEST_ID.OPEN_FLOW_LINK}
      >
        {content}
      </StaticActor>
    );
  }

  const flowPath = `${FLOW_BUILDER_UPDATE_PATH}${flowBlueprintId}?actionId=${flowBlueprintActionId}`;

  return (
    <ActorLink
      openInNewTab
      to={flowPath}
      dataTestId={TEST_ID.OPEN_FLOW_LINK}
      title={matchedFlow.name}
    >
      {content}
    </ActorLink>
  );
};

/** Reads as metadata next to the date, not as the card's primary action */
const actorStyles = ({ theme }: { theme: DefaultTheme }) => css`
  display: inline-flex;
  align-items: center;
  gap: ${theme.space('xxs')};
  /** Allows the flow name to truncate instead of pushing the date around */
  min-width: 0;
  max-width: 100%;
  color: ${theme.color('tertiary', 'dark')};
`;

const StaticActor = styled.span<{}>(actorStyles);

const ActorLink = styled(Link)<{}>(
  ({ theme }) => css`
    ${actorStyles({ theme })};

    &:hover {
      color: ${theme.color('secondary')};
    }
  `,
);

const Prefix = styled(Body)(
  () => css`
    flex-shrink: 0;
    color: inherit;
  `,
);

const StyledIcon = styled(Icon)<{}>(
  () => css`
    flex-shrink: 0;
  `,
);

const FlowName = styled(Body)(
  () => css`
    /** Needed for ellipsis truncation inside a flex row */
    min-width: 0;
    color: inherit;
  `,
);

export default FlowActorLink;
